/**
 * Recherche du coffre, comme celle de Bitwarden (`bitwarden/clients` @ 245879a5e326,
 * `libs/common/src/vault/services/search.service.ts`, `searchCiphersBasic` et `normalizeSearchQuery`) :
 * - insensible a la casse et aux accents : « societe » trouve « Société », « Elodie » trouve « Élodie » ;
 * - plusieurs mots : CHAQUE mot doit se trouver dans un champ cherche, pas forcement le meme ni dans l'ordre
 *   (« ana banque » trouve l'entree « Banque » dont l'identifiant est « ana ») ;
 * - recherche avancee, comme celle de Bitwarden (`isSearchable` : une demande qui commence par « > » passe par la
 *   syntaxe lunr) : `>nom:banque`, `>site:sg.fr`, `>-perso` (exclure), `>"lyon bellecour"` (expression exacte).
 *
 * Module pur : il ne decide pas QUELS champs sont cherches (c'est `matchesQuery` de `vault-store.ts`, qui n'y met
 * jamais un champ sensible), seulement COMMENT un texte est compare. Tout se passe en memoire, sur le dechiffre.
 */

/**
 * Texte tel que la recherche le compare : minuscules, marques diacritiques retirees (decomposition NFD), et les
 * lettres que NFD ne decompose pas (ligatures « œ », « æ », « ß » ; « ø », « ł », « đ », « ı » des langues
 * nordiques, polonaise, croate et turque) ecrites en lettres latines simples, pour que « oeuvre » trouve « Œuvre ».
 */
export function foldSearchText(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/œ/g, 'oe')
    .replace(/æ/g, 'ae')
    .replace(/ß/g, 'ss')
    .replace(/ø/g, 'o')
    .replace(/ł/g, 'l')
    .replace(/đ/g, 'd')
    .replace(/ı/g, 'i');
}

/** Mots de la demande, repliés comme les textes cherches ; aucun mot = tout correspond. */
export function searchTerms(query: string): string[] {
  return foldSearchText(query).split(/\s+/u).filter((term) => term.length > 0);
}

/** Vrai si chaque mot de la demande se trouve dans au moins un des textes (pas forcement le meme). */
export function matchesSearchTerms(haystacks: readonly string[], terms: readonly string[]): boolean {
  if (terms.length === 0) return true;
  const folded = haystacks.map(foldSearchText);
  return terms.every((term) => folded.some((value) => value.includes(term)));
}

/** Champ que la recherche avancee peut viser ; `field` = champs types non sensibles et champs personnalises. */
export type SearchField = 'name' | 'username' | 'uri' | 'notes' | 'tag' | 'field';

/** Textes cherches d'une entree, ranges par champ -- `matchesQuery` n'y met jamais un champ sensible. */
export type SearchableTexts = Readonly<Record<SearchField, readonly string[]>>;

export interface AdvancedSearchTerm {
  /** `null` = n'importe quel champ cherche. */
  readonly field: SearchField | null;
  /** Mot ou expression exacte, replie comme les textes cherches. */
  readonly text: string;
  /** « -mot » : l'entree NE doit PAS le contenir. */
  readonly exclude: boolean;
  /** Prefixe « xxx: » qui n'est pas un nom de champ connu : le terme est cherche comme texte, l'IHM le signale. */
  readonly unknownField?: string;
}

/** Nom de champ montre a l'utilisateur pour chaque champ visable (le premier alias francais de chacun). */
export const SEARCH_FIELD_LABELS: Readonly<Record<SearchField, string>> = {
  name: 'nom',
  username: 'identifiant',
  uri: 'site',
  notes: 'notes',
  tag: 'etiquette',
  field: 'champ',
};

/**
 * Noms de champ reconnus devant « : », replies (« étiquette » = « etiquette ») : francais, et ceux de Bitwarden
 * (`name`, `login.username`, `login.uris`, `notes`, `fields`) pour qu'une habitude venue de chez lui marche ici.
 */
const SEARCH_FIELD_ALIASES: Readonly<Record<string, SearchField>> = {
  nom: 'name',
  titre: 'name',
  name: 'name',
  identifiant: 'username',
  utilisateur: 'username',
  username: 'username',
  'login.username': 'username',
  site: 'uri',
  url: 'uri',
  uri: 'uri',
  uris: 'uri',
  'login.uris': 'uri',
  note: 'notes',
  notes: 'notes',
  etiquette: 'tag',
  etiquettes: 'tag',
  tag: 'tag',
  tags: 'tag',
  champ: 'field',
  champs: 'field',
  field: 'field',
  fields: 'field',
};

/** Vrai si la demande commence par « > » : elle suit la syntaxe avancee, comme chez Bitwarden. */
export function isAdvancedSearch(query: string): boolean {
  return query.trimStart().startsWith('>');
}

/**
 * Demande avancee decoupee en termes : `[-|+][champ:]mot` ou `[-|+][champ:]"expression exacte"`. Un prefixe qui
 * n'est pas un nom de champ connu reste du texte (« https://sg.fr » est un mot, pas un champ « https ») ; un terme
 * vide (« nom: » en cours de frappe, « "" ») est ignore plutot que de vider la liste.
 */
export function parseAdvancedSearch(query: string): AdvancedSearchTerm[] {
  const body = query.trimStart().replace(/^>/u, '');
  const terms: AdvancedSearchTerm[] = [];
  for (const match of body.matchAll(/([+-]?)(?:([^\s:"]+):)?(?:"([^"]*)(?:"|$)|([^\s"]*))/gu)) {
    const [, sign, prefix, quoted, word] = match;
    const field = prefix === undefined ? null : (SEARCH_FIELD_ALIASES[foldSearchText(prefix)] ?? null);
    const raw = quoted ?? word ?? '';
    const text = foldSearchText(prefix !== undefined && field === null ? `${prefix}:${raw}` : raw).trim();
    if (text.length === 0) continue;
    // « https://sg.fr » est une adresse, pas une tentative de champ : rien a signaler.
    const unknown = prefix !== undefined && field === null && !raw.startsWith('//');
    terms.push(unknown ? { field, text, exclude: sign === '-', unknownField: prefix } : { field, text, exclude: sign === '-' });
  }
  return terms;
}

/** Vrai si chaque terme est trouve dans le champ vise (n'importe lequel sans champ) -- absent pour un « -terme ». */
export function matchesAdvancedSearch(texts: SearchableTexts, terms: readonly AdvancedSearchTerm[]): boolean {
  const folded = new Map((Object.keys(texts) as SearchField[]).map((field) => [field, texts[field].map(foldSearchText)]));
  const everywhere = [...folded.values()].flat();
  return terms.every((term) => {
    const pool = term.field === null ? everywhere : (folded.get(term.field) ?? []);
    const found = pool.some((value) => value.includes(term.text));
    return term.exclude ? !found : found;
  });
}
